// Lightweight, vendor-agnostic analytics layer
// Never send PII. Keep payloads minimal.

import { ConsentState, getEffectiveConsent } from './consent';
import { analytics } from '../firebase';
import { logEvent, setAnalyticsCollectionEnabled } from 'firebase/analytics';
import i18n from 'i18next';

type AnalyticsProvider = 'console' | 'beacon' | 'firebase';

interface CommonEvent {
  type: string;
  ts?: number; // ms epoch
  path?: string;
  lang?: string;
}

let consent: ConsentState = getEffectiveConsent();
let initialized = false;

const PROVIDER: AnalyticsProvider = (process.env.REACT_APP_ANALYTICS_PROVIDER as AnalyticsProvider) || 'firebase';
const BEACON_URL = process.env.REACT_APP_ANALYTICS_BEACON_URL || '';
const SITE_ID = process.env.REACT_APP_ANALYTICS_SITE_ID || 'universaldrive';
const ENV = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'production';
const SITE_DOMAIN = process.env.REACT_APP_SITE_DOMAIN || (typeof window !== 'undefined' ? window.location.hostname : undefined) || 'unknown';
const EVENT_VERSION = 'v1';

function now() {
  return Date.now();
}

function base(ev: Partial<CommonEvent>): CommonEvent {
  return {
    type: ev.type || 'event',
    ts: now(),
    path: ev.path || (typeof window !== 'undefined' ? window.location.pathname : undefined),
    lang: i18n.language || (navigator && (navigator as any).language) || undefined,
  } as CommonEvent;
}

function send(event: Record<string, any>) {
  if (consent !== 'granted') {
    setAnalyticsCollectionEnabled(analytics, false);
    return;
  }
  setAnalyticsCollectionEnabled(analytics, true);

  if (PROVIDER === 'firebase') {
    const { type, ...props } = enrich(event);
    logEvent(analytics, type, props);
    return;
  }

  if (PROVIDER === 'console') {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[analytics]', enrich(event));
    }
    return;
  }
  if (PROVIDER === 'beacon' && BEACON_URL) {
    const payload = JSON.stringify({ site: SITE_ID, ...enrich(event) });
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(BEACON_URL, blob);
    } else {
      fetch(BEACON_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
    }
  }
}

export function setConsent(state: ConsentState) {
  consent = state;
}

export function init() {
  if (initialized) return;
  initialized = true;
}

export function trackPageview(path?: string) {
  if (PROVIDER === 'firebase') {
    logEvent(analytics, 'page_view', {
      page_path: path || window.location.pathname,
      ...enrich({})
    });
    return;
  }
  send({ ...base({ type: 'pageview', path }) });
}

export function trackEvent(name: string, props?: Record<string, any>) {
  send({ ...base({ type: name }), ...sanitizeProps(props) });
}

function sanitizeProps(props?: Record<string, any>) {
  // Ensure we never accidentally send PII-like keys
  const banned = ['email', 'phone', 'message', 'name'];
  const out: Record<string, any> = {};
  if (!props) return out;
  for (const [k, v] of Object.entries(props)) {
    if (banned.includes(k.toLowerCase())) continue;
    // coerce long strings
    if (typeof v === 'string' && v.length > 128) {
      out[k] = v.substring(0, 125) + '...';
    } else {
      out[k] = v;
    }
  }
  return out;
}

function getReferrerOrigin(): string | undefined {
  try {
    if (typeof document === 'undefined' || !document.referrer) return undefined;
    const u = new URL(document.referrer);
    return u.origin;
  } catch {
    return undefined;
  }
}

function getUserAgent(): string | undefined {
  try {
    const ua = (navigator && (navigator as any).userAgent) || '';
    if (!ua) return undefined;
    return ua.length > 180 ? ua.substring(0, 177) + '...' : ua;
  } catch {
    return undefined;
  }
}

function enrich(event: Record<string, any>): Record<string, any> & CommonEvent {
  return {
    type: 'event', // default type
    event_version: EVENT_VERSION,
    env: ENV,
    site_domain: SITE_DOMAIN,
    referrer: getReferrerOrigin(),
    ua: getUserAgent(),
    lang: i18n.language || (navigator && (navigator as any).language) || undefined,
    ...event,
  } as any;
}
